import React from 'react';
import { FixedTail, ToggleWrapper, TruncatingHead } from './ToggleItem.styles';
import type { TextV2Props } from '../../../TextV2';
import { TextV2 } from '../../../TextV2';
import { pxToRem } from '@withjoy/joykit/theme';
import classNames from 'classnames';
import type { SegmentedControllerVariant } from '../../SegmentedController.types';

export type ToggleItemProps = Readonly<{
  label: string;
  value: string;
  valueTail?: string;
  description?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  isActive?: boolean;
  isDisabled?: boolean;
  isMobile?: boolean;
  textProps?: TextV2Props;
  variant?: SegmentedControllerVariant;
}>;

const UNDERLINE_TEXT_PROPS: TextV2Props = {
  typographyVariant: 'button2',
  fontSize: pxToRem(15),
};

const PILL_TEXT_PROPS: TextV2Props = {
  typographyVariant: 'body1',
};

/**
 * With a description the item carries two lines in one cell, so the label drops from the
 * single-line 15px to the 13px semibold that sits above a 13px regular description.
 */
const DESCRIPTION_TEXT_PROPS: TextV2Props = {
  typographyVariant: 'label3',
};

/**
 * A real `<button>`, not the `TextV2 as="a"` this used to render. That anchor carried no `href` and
 * no `tabIndex`, so no segmented controller in the app was reachable by keyboard -- an operability
 * failure (WCAG 2.1.1), not a styling one.
 *
 * `aria-pressed` rather than tab semantics: the control is used both as a tab strip and as a filter
 * row, and `role="tablist"` would oblige every consumer to also own arrow-key roving focus and
 * panel wiring. A group of toggle buttons is honest about what this actually is.
 *
 * `data-toggle-item` is the sizing hook for consumers that need a taller item than the shared 36px.
 * Two of them reached in as `& a`, and the anchor -> button change silently broke both: the
 * selector simply stopped matching and the control fell back to the default height, with nothing
 * to fail. Target the attribute rather than the tag so the element stays an implementation detail.
 */
/**
 * A description item is two lines, always: the label on one, the description on the other. Two
 * items side by side must stay the same height, and a label left to wrap made one tab three lines
 * tall beside a two-line neighbour. The label's head truncates; its tail never does.
 */
const ONE_LINE_ROW_PROPS: TextV2Props = { display: 'flex', justifyContent: 'center', width: '100%', minWidth: 0 };
const ONE_LINE_TEXT_PROPS: TextV2Props = { width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const ToggleItem: React.FC<ToggleItemProps> = ({ onClick, isActive, value, valueTail, description, isMobile, textProps = {}, isDisabled, variant }) => {
  const isUnderline = variant === 'underline';
  const baseTextProps = description ? DESCRIPTION_TEXT_PROPS : isUnderline ? UNDERLINE_TEXT_PROPS : PILL_TEXT_PROPS;
  const inactiveColor = isUnderline && !description ? 'mono8' : 'mono10';

  return (
    <ToggleWrapper
      as="button"
      type="button"
      aria-pressed={isActive}
      disabled={isDisabled}
      data-toggle-item=""
      isMobile={isMobile}
      variant={variant}
      hasDescription={Boolean(description)}
      onClick={isDisabled ? undefined : onClick}
      className={classNames({ active: isActive, disabled: isDisabled })}
    >
      <TextV2
        color={isActive ? 'mono14' : inactiveColor}
        {...baseTextProps}
        {...textProps}
        {...(description ? ONE_LINE_ROW_PROPS : undefined)}
      >
        {description ? (
          <>
            <TruncatingHead>{value}</TruncatingHead>
            {/* The gap belongs to the tail (`pre` keeps the leading space), so it survives the
                ellipsis and the label still reads as two words to assistive tech. */}
            {valueTail ? <FixedTail>{` ${valueTail}`}</FixedTail> : null}
          </>
        ) : (
          value
        )}
      </TextV2>
      {description ? (
        <TextV2
          typographyVariant="label2"
          color="mono10"
          {...ONE_LINE_TEXT_PROPS}
        >
          {description}
        </TextV2>
      ) : null}
    </ToggleWrapper>
  );
};

export { ToggleItem };
